// src/apps/colonel/composables/useTenantsAdmin.ts
//
// Composable para el panel admin de tenants (secrets-files fork).
// Wrapeae los endpoints /api/colonel/tenants/*.

import { ref } from 'vue'
import { useApi } from '@/shared/composables/useApi'

export interface Tenant {
  slug: string
  display_name: string
  objid: string
  planid: string
  suspended: boolean
  domains: string[]
  member_count: number
  created: number
  updated: number | null
}

export interface CertStatus {
  display_domain: string
  host: string
  port: number
  ok: boolean
  subject_cn?: string
  issuer?: string
  not_before?: string
  not_after?: string
  days_to_expiry?: number
  san?: string[]
  error?: string
}

export interface CreateTenantPayload {
  slug: string
  display_name: string
  display_domain: string
  planid?: string
}

export interface UpdateTenantPayload {
  display_name?: string
  planid?: string
}

export function useTenantsAdmin() {
  const $api = useApi()

  const tenants = ref<Tenant[]>([])
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  function captureError(ex: unknown, fallback: string): string {
    const obj = ex as { response?: { data?: { message?: string; errors?: Record<string, string> } } }
    const msg = obj.response?.data?.message
    if (msg) return msg
    const fieldErr = obj.response?.data?.errors
    if (fieldErr) {
      const k = Object.keys(fieldErr)[0]
      if (k) return `${k}: ${fieldErr[k]}`
    }
    return ex instanceof Error ? ex.message : fallback
  }

  async function fetchTenants(): Promise<void> {
    isLoading.value = true
    error.value = null
    try {
      const response = await $api.get<{ tenants: Tenant[] }>('/api/colonel/tenants')
      tenants.value = response.data.tenants
    } catch (ex) {
      error.value = captureError(ex, 'Failed to load tenants')
      tenants.value = []
    } finally {
      isLoading.value = false
    }
  }

  async function createTenant(payload: CreateTenantPayload): Promise<Tenant> {
    error.value = null
    try {
      const response = await $api.post('/api/colonel/tenants', payload)
      await fetchTenants()
      return response.data
    } catch (ex) {
      error.value = captureError(ex, 'Failed to create tenant')
      throw ex
    }
  }

  async function updateTenant(slug: string, payload: UpdateTenantPayload): Promise<void> {
    error.value = null
    try {
      await $api.patch(`/api/colonel/tenants/${encodeURIComponent(slug)}`, payload)
      await fetchTenants()
    } catch (ex) {
      error.value = captureError(ex, 'Failed to update tenant')
      throw ex
    }
  }

  async function deleteTenant(slug: string): Promise<void> {
    error.value = null
    try {
      await $api.delete(`/api/colonel/tenants/${encodeURIComponent(slug)}`)
      await fetchTenants()
    } catch (ex) {
      error.value = captureError(ex, 'Failed to delete tenant')
      throw ex
    }
  }

  async function setSuspended(slug: string, suspended: boolean): Promise<void> {
    error.value = null
    const path = suspended ? 'suspend' : 'unsuspend'
    try {
      await $api.post(`/api/colonel/tenants/${encodeURIComponent(slug)}/${path}`)
      await fetchTenants()
    } catch (ex) {
      error.value = captureError(ex, 'Failed to toggle suspended')
      throw ex
    }
  }

  async function fetchCertStatus(slug: string): Promise<{ slug: string; certs: CertStatus[] }> {
    error.value = null
    try {
      const response = await $api.get(`/api/colonel/tenants/${encodeURIComponent(slug)}/cert-status`)
      return response.data
    } catch (ex) {
      error.value = captureError(ex, 'Failed to fetch cert status')
      throw ex
    }
  }

  return {
    tenants,
    isLoading,
    error,
    fetchTenants,
    createTenant,
    updateTenant,
    deleteTenant,
    setSuspended,
    fetchCertStatus,
  }
}
